import { useId, useMemo, useState } from 'react';
import { imageCandidates } from '@/lib/imageCandidates';
import { mediaUrl } from '@/lib/media';

/**
 * Generative "aura" avatars.
 *
 * We have no photography, and stock faces would be a lie on a platform where
 * every companion is a real verified person. So each avatar is a deterministic
 * warm gradient field derived from the companion's hue — recognisable, never
 * repeated, and it scales to any size without a raster asset.
 */

interface AvatarProps {
  initials: string;
  hue: number;
  size?: number;
  /** Adds a soft breathing halo. Used for people who are free to talk. */
  active?: boolean;
  className?: string;
  /**
   * Filename in public/images/ — e.g. "companion-c1.jpg". If the file exists
   * it wins; if not, we fall back to the generated field. That means real
   * portraits can be added one at a time without a code change.
   *
   * ANY aspect ratio works. The image is centre-cropped to a square and
   * masked to a circle, so a 3:4 or 2:3 portrait straight out of a camera or
   * a generator is fine — no need to square it first.
   */
  photo?: string;
  alt?: string;
  /**
   * Where the crop is anchored, as `object-position`. Defaults to slightly
   * above centre because faces sit in the upper third of a portrait — a plain
   * centre crop on a tall image slices the top of the head off.
   *
   * Override per person when a particular photo needs it, e.g. "50% 20%".
   */
  photoPosition?: string;
}

export default function Avatar({
  initials,
  hue,
  size = 48,
  active = false,
  className = '',
  photo,
  alt,
  photoPosition = '50% 35%',
}: AvatarProps) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const h2 = (hue + 38) % 360;
  const h3 = (hue + 320) % 360;

  /*
   * Two kinds of value arrive here.
   *
   * An uploaded photo is a storage key — `profile-photos/<uid>/<uuid>.jpg` —
   * whose exact filename we chose, so there is nothing to guess: resolve it
   * once through mediaUrl and use it.
   *
   * A seeded demo portrait is a bare basename supplied by hand, where the
   * extension may be .jpg, .png or .webp. Those still go through the
   * extension-guessing fallback.
   */
  const candidates = useMemo(() => {
    if (!photo) return [];
    const resolved = mediaUrl(photo);
    if (!resolved) return [];

    // Fully-formed src values, so the <img> below never has to know which kind
    // it got. Prefixing /images/ onto a storage URL was a bug waiting to
    // happen the first time someone uploaded a photo.
    return photo.includes('/')
      ? [resolved]
      : imageCandidates(photo).map((name) => `/images/${name}`);
  }, [photo]);
  const [attempt, setAttempt] = useState(0);
  const photoSrc = candidates[attempt];

  return (
    <span
      className={`relative inline-flex shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      {active && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background: `oklch(0.72 0.13 ${hue})`,
            animation: 'breathe 3.4s ease-in-out infinite',
          }}
        />
      )}

      {photoSrc && (
        <img
          src={photoSrc}
          alt={alt ?? initials}
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          // Try the next extension; once exhausted the generated field shows.
          onError={() => setAttempt((i) => i + 1)}
          className="relative rounded-full object-cover"
          style={{
            width: size,
            height: size,
            objectPosition: photoPosition,
            boxShadow: 'inset 0 0 0 1px rgb(0 0 0 / 0.06)',
          }}
        />
      )}

      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className="relative rounded-full"
        style={{
          boxShadow: 'inset 0 0 0 1px rgb(0 0 0 / 0.06)',
          display: photoSrc ? 'none' : undefined,
        }}
        role="img"
        aria-label={initials}
      >
        <defs>
          <radialGradient id={`base${uid}`} cx="30%" cy="22%" r="95%">
            <stop offset="0%" stopColor={`oklch(0.93 0.07 ${hue})`} />
            <stop offset="55%" stopColor={`oklch(0.83 0.11 ${hue})`} />
            <stop offset="100%" stopColor={`oklch(0.68 0.13 ${h2})`} />
          </radialGradient>

          <radialGradient id={`glow${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={`oklch(0.95 0.09 ${h3})`} stopOpacity="0.85" />
            <stop offset="100%" stopColor={`oklch(0.95 0.09 ${h3})`} stopOpacity="0" />
          </radialGradient>

          <clipPath id={`clip${uid}`}>
            <circle cx="50" cy="50" r="50" />
          </clipPath>
        </defs>

        <g clipPath={`url(#clip${uid})`}>
          <rect width="100" height="100" fill={`url(#base${uid})`} />
          {/* Two soft light pools give the field depth without looking noisy */}
          <ellipse cx="74" cy="30" rx="42" ry="36" fill={`url(#glow${uid})`} />
          <ellipse cx="22" cy="84" rx="46" ry="34" fill={`url(#glow${uid})`} opacity="0.6" />
          {/* Top highlight — the thing that makes it read as a lit object */}
          <ellipse cx="50" cy="-8" rx="60" ry="34" fill="#fff" opacity="0.22" />
        </g>

        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="var(--font-display)"
          fontSize="36"
          fontWeight="600"
          letterSpacing="0.5"
          fill={`oklch(0.32 0.08 ${hue})`}
          opacity="0.92"
        >
          {initials}
        </text>
      </svg>
    </span>
  );
}
